import {
  ChangeDetectionStrategy, Component, EventEmitter, Input,
  OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  add,
  endOfWeek,
  set,
  setHours,
  startOfWeek,
  sub
} from 'date-fns';
import { CalendarEventResponseCoreFragment } from 'generated/graphql';
import { ListEventsDialog } from '../../dialogs/list-events/list-events.dialog';

@Component({
  selector: 'app-week-calendar',
  templateUrl: './week-calendar.component.html',
  styleUrls: ['./week-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class WeekCalendarComponent implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() events;
  @Input() loading = false;
  @Input() focusDate = new Date();
  @Output() visibleDatesChanged = new EventEmitter<{ focusDate: Date; startDate: Date; endDate: Date }>();
  @Output() eventResponseUpdated: EventEmitter<CalendarEventResponseCoreFragment> = new EventEmitter<CalendarEventResponseCoreFragment>();
  today = new Date();
  firstOfWeek = startOfWeek(this.today);
  lastOfWeek = endOfWeek(this.today);
  currentDate = this.today;

  constructor(private _dialog: MatDialog, private _route: ActivatedRoute, private _router: Router) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.events) {
      this.updateDates(this.focusDate, false);
    }
  }

  ngOnInit() {
    this.updateDates(this.focusDate);
  }

  updateDates(date: Date, emitEvent = true) {
    this.firstOfWeek = startOfWeek(date);
    this.lastOfWeek = endOfWeek(date);
    this.firstOfWeek = setHours(this.firstOfWeek, 0);
    this.lastOfWeek = set(this.lastOfWeek, { hours: 23, minutes: 59, seconds: 59 });
    this.currentDate = this.firstOfWeek;
    if (emitEvent) {
      this.visibleDatesChanged.emit({ focusDate: this.firstOfWeek, startDate: this.firstOfWeek, endDate: this.lastOfWeek });
    }
  }

  /** Formats date range display */
  formatDateRange(start: Date, end: Date, fullMonth: boolean, showYear: boolean): string {
    const startMonth = start.toLocaleString('default', { month: fullMonth ? 'long' : 'short' });
    const endMonth = end.toLocaleString('default', { month: fullMonth ? 'long' : 'short' });
    const startDate = start.getDate();
    const endDate = end.getDate();
    const year = end.getFullYear();

    if (startMonth === endMonth) {
      // If same month, only show month on start date
      return showYear
        ? `${startMonth} ${startDate} - ${endDate}, ${year}`
        : `${startMonth} ${startDate} - ${endDate}`;
    } else {
      // Otherwise, show both months
      return showYear
        ? `${startMonth} ${startDate} - ${endMonth} ${endDate}, ${year}`
        : `${startMonth} ${startDate} - ${endMonth} ${endDate}`;
    }
  }

  jumpToDate(date = new Date()) {
    this.updateDates(date);
  }

  prevWeek() {
    const weekBefore = sub(this.currentDate, { weeks: 1 });
    this.updateDates(weekBefore);
  }

  nextWeek() {
    const weekAfter = add(this.currentDate, { weeks: 1 });
    this.updateDates(weekAfter);
  }

  viewSingleEvent(event) {
    this._router.navigate(['/events/view'], {
      queryParams: {
        campaignId: this._route.snapshot.queryParams['campaignId'],
        eventId: event.id
      }
    });
  }

  openListEventsDialog(data) {
    if (!data.events || data.events.length === 0) { return; }
    // Open dialog if more than one event, or viewing entity's events
    if (this.entityId || data.events.length > 1) {
      const dialog = ListEventsDialog.open(this._dialog, { data: { day: data, entityId: this.entityId } });
      dialog.componentInstance.onEventResponseUpdate.subscribe((response) => {
        this.eventResponseUpdated.emit(response);
      });
    } else if (data.events.length === 1) {
      // Single event on manage page, view it
      this._router.navigate(['/events/view'], {
        queryParams: {
          campaignId: this._route.snapshot.queryParams['campaignId'],
          eventId: data.events[0].id
        }
      });
    }
  }
}
